import {
    faSortAlphaAsc,
    faSortAlphaDesc,
    faSortNumericAsc,
    faSortNumericDesc,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colors } from '@kcftech/design-tokens';
import { BarChartLegend } from 'pages/Dashboard/components/BarChartLegend';
import {
    SegmentConfig,
    StackedHorizontalBarChart,
} from 'pages/Dashboard/components/StackedHorizontalBarChart';
import {
    ChartContainer,
    SegmentButton,
    SegmentedControl,
    ViewToggleContainer,
} from 'pages/Dashboard/components/styled';
import { VerticalScrollableBarChart } from 'pages/Dashboard/components/VerticalScrollableBarChart';
import { useCallback, useMemo, useState } from 'react';

type LocationTicketStatus = {
    LocationId: string;
    LocationName: string;
    OpenCount: number;
    ClosedCount: number;
    NewCount: number;
};

type ChartDataRow = {
    name: string;
    New: number;
    Open: number;
    Closed: number;
};

type RowSortFn = (a: ChartDataRow, b: ChartDataRow) => number;

export function OpenClosedNewTickets({ data }: { data: LocationTicketStatus[] }) {
    type SortField = 'name' | 'count';
    type SortDirection = 'asc' | 'desc';
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const sortFunction = useMemo<RowSortFn>(() => {
        {
            switch (`${sortField}-${sortDirection}` as const) {
                case 'name-asc':
                    return (a, b) => a.name.localeCompare(b.name);
                case 'name-desc':
                    return (a, b) => b.name.localeCompare(a.name);
                // `-` on the Closed, because negative numbers are used in this
                // charting library to render bars to the left of the zero
                // (which is reasonable)
                case 'count-asc':
                    return (a, b) =>
                        a.Open + -a.Closed + a.New - (b.Open + -b.Closed + b.New);
                case 'count-desc':
                    return (a, b) =>
                        b.Open + -b.Closed + b.New - (a.Open + -a.Closed + a.New);
            }
        }
    }, [sortField, sortDirection]);

    const mappedData = useMemo(
        () =>
            data.map((location) => ({
                name: location.LocationName,
                Open: location.OpenCount,
                New: location.NewCount,
                Closed: -location.ClosedCount,
            })),
        [data],
    );

    const chartData = useMemo(
        () => mappedData.toSorted(sortFunction),
        [mappedData, sortFunction],
    );

    const nextSortState = useCallback((field: SortField, currentDir: SortDirection) => {
        setSortField((curField) => {
            if (curField !== field) {
                // field is changing, set to ascending by default
                setSortDirection('asc');
                return field;
            }

            // a click on the current field, change the sort direction
            setSortDirection(currentDir === 'asc' ? 'desc' : 'asc');
            return field;
        });
    }, []);

    const segments: SegmentConfig<ChartDataRow>[] = useMemo(
        () => [
            {
                dataKey: 'Closed',
                label: 'Closed',
                color: colors.blue[600],
                textColor: colors.blue[100],
                stackId: 'issues',
            },
            {
                dataKey: 'Open',
                label: 'Open',
                color: colors.blue[400],
                textColor: colors.blue[50],
                stackId: 'issues',
            },
            {
                dataKey: 'New',
                label: 'New',
                color: colors.blue[200],
                stackId: 'issues',
            },
        ],
        [],
    );

    return (
        <ChartContainer style={{ minHeight: 0, position: 'relative' }}>
            <ViewToggleContainer>
                <SegmentedControl>
                    <SegmentButton
                        $isActive={sortField === 'name'}
                        onClick={() => nextSortState('name', sortDirection)}
                        aria-label="Sort by name"
                        title="Sort by name"
                    >
                        <FontAwesomeIcon
                            icon={
                                sortField === 'name' && sortDirection === 'desc'
                                    ? faSortAlphaDesc
                                    : faSortAlphaAsc
                            }
                        />
                    </SegmentButton>
                    <SegmentButton
                        $isActive={sortField === 'count'}
                        onClick={() => nextSortState('count', sortDirection)}
                        aria-label="Sort by ticket count"
                        title="Sort by ticket count"
                    >
                        <FontAwesomeIcon
                            icon={
                                sortField === 'count' && sortDirection === 'desc'
                                    ? faSortNumericDesc
                                    : faSortNumericAsc
                            }
                        />
                    </SegmentButton>
                </SegmentedControl>
            </ViewToggleContainer>

            <VerticalScrollableBarChart
                xAxisHeight={30}
                dataLength={chartData.length}
                minBarHeight={42}
                legend={
                    <BarChartLegend
                        items={[
                            {
                                color: colors.blue[600],
                                label: 'Closed',
                            },
                            { color: colors.blue[400], label: 'Open' },
                            { color: colors.blue[200], label: 'New' },
                        ]}
                    />
                }
            >
                {({ xAxisHeight }) => (
                    <StackedHorizontalBarChart
                        data={chartData}
                        segments={segments}
                        categoryKey="name"
                        yAxisWidth={140}
                        xAxisHeight={xAxisHeight}
                        stackOffset="sign"
                        showGrid={true}
                        showTooltip={true}
                        rounding="both"
                        cornerRadius={5}
                        showBarLabels
                        xAxisTickFormatter={(value) => Math.abs(value).toLocaleString()}
                        cleanValue={Math.abs}
                    />
                )}
            </VerticalScrollableBarChart>
        </ChartContainer>
    );
}
